import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { toast } from "react-toastify";
import axios from "axios";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import BasicTabs from "../../components/Trials/BasicTabs";
import EcrfVisitsV2 from "../Patient/v2/ecrf/EcrfVisitsV2";
import { __API_URL__ } from "../../SERVER_URL";
import { useEcrfContext } from "../../context/EcrfContext";
import useGetVisits from "../../hooks/useGetVisits";
import useGetTrialForPatient from "../../hooks/useGetTrialForPatient";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation";
import { CustomDatePicker } from "../../components/UI/CustomDatePicker";

dayjs.extend(utc);

const FullForm = () => {
  const { infoData, trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();
  const { _id_ } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    setActive("ecrf");
    setBreadcrumbTitle("eCRF");
    setPatientId(_id_);
  }, [_id_]);

  useEffect(() => {
    setSelectedSessionId(0);
    setValue(0);
  }, [trialFilter]);

  const {
    selectedVisit,
    setSelectedVisit,
    setZeroVisit,
    setEventList,
    initialFlag,
    setInitialFlag,
    trial,
    setTrial,
    setPatientId,
    setAllVisits,
    setHasChanged,
  } = useEcrfContext();

  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);

  const token = localStorage.getItem("token");

  const logAction = useUserActionsLogsMutation();

  const [message, setMessage] = useState("");
  const [typeOfMessage, setTypeOfMessage] = useState("");
  const [modalVisitChangeShow, setModalVisitChangeShow] = useState(false);
  const [value, setValue] = useState(0);
  const [numberOfVisitsArray, setNumberOfVisitsArray] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(0);
  const [scheduleBaselineModalShow, setScheduleBaselineModalShow] = useState(false);
  const [visitStartDateTime, setVisitStartDateTime] = useState(
    dayjs().startOf("day").toISOString()
  );

  const { data: trialData, refetch: refetchTrial } = useGetTrialForPatient({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    if (trialData) {
      setTrial(trialData?.trial);
    }
  }, [trialData]);

  const processedTrialIdRef = useRef(undefined);

  const { data: visits } = useGetVisits({
    trialId: trial?.id,
    patientId: _id_,
    selectedSessionId: selectedSessionId,
    enabled: !!trial?.id && !!_id_,
  });

  useEffect(() => {
    if (!!visits) {
      initialDataProcessing(visits);
    }
  }, [visits]);

  const initialDataProcessing = (data) => {
    setAllVisits(data);

    const screeningVisitSelected = data.filter((visit) => {
      return visit.visit_number === -3;
    });

    const zeroVisitSelected = data.filter((visit) => {
      return visit.visit_number === 0;
    });

    setZeroVisit(zeroVisitSelected[0]);

    const isNewTrial = processedTrialIdRef.current !== trial?.id;
    processedTrialIdRef.current = trial?.id;

    if (initialFlag || isNewTrial) {
      if (data.length > 0) {
        let filteredData = data.filter((item) => [-1, -2, -3, -4, -6].includes(item.visit_number));
        let visitNumbers = filteredData.map((item) => item.visit_number);
        setEventList(visitNumbers);

        if (screeningVisitSelected.length > 0) {
          setSelectedVisit({
            ...screeningVisitSelected[0],
            start_date: new Date(screeningVisitSelected[0].start_date + "Z"),
          });
        } else if (zeroVisitSelected.length > 0) {
          setSelectedVisit({
            ...zeroVisitSelected[0],
            start_date: new Date(zeroVisitSelected[0].start_date + "Z"),
          });
        }
      } else {
        setHasChanged(true);
      }
    } else {
      const concernedVisitSelected = data.filter((visit) => {
        return visit.visit_number === selectedVisit?.visit_number;
      });
      if (concernedVisitSelected.length > 0) {
        setSelectedVisit({
          ...concernedVisitSelected[0],
          start_date: new Date(concernedVisitSelected[0].start_date + "Z"),
        });
      }
    }

    setInitialFlag(false);
  };

  const handleBaselineStartDate = () => {
    logAction.mutate({
      action_type: `Schedule Baseline Visit saved.`,
      trial_name: trial.name,
      patient_id: infoData?.email,
    });

    let dateToSend = visitStartDateTime;
    if (trial?.enforce_visit_start_time_at_12 && visitStartDateTime) {
      dateToSend = dayjs(visitStartDateTime).startOf("day").utc().toISOString();
    }

    axios
      .post(
        `${__API_URL__}/api/patient/patient/save_visit_start_date/`,
        {
          visit_start_date: dateToSend,
          patientId: _id_,
          trialId: trial?.id,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        setInitialFlag(true);
        refetchTrial();
        queryClient.invalidateQueries({ queryKey: ["visits"] });
        queryClient.invalidateQueries({ queryKey: ["visit_overview"] });
        toast.success("Start Date Saved Successfully");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      });
  };

  return (
    <>
      <Box>
        {trial?.id !== 0 && infoData.ui_version === "v2" && (
          <EcrfVisitsV2
            setMessage={setMessage}
            setTypeOfMessage={setTypeOfMessage}
            modalVisitChangeShow={modalVisitChangeShow}
            setModalVisitChangeShow={setModalVisitChangeShow}
            setScheduleBaselineModalShow={setScheduleBaselineModalShow}
            singlePhotoSession={infoData.single_photo_session}
          />
        )}
        {trial?.id !== 0 && infoData.ui_version !== "v2" && (
          <BasicTabs
            setMessage={setMessage}
            setTypeOfMessage={setTypeOfMessage}
            value={value}
            setValue={setValue}
            numberOfVisitsArray={numberOfVisitsArray}
            setNumberOfVisitsArray={setNumberOfVisitsArray}
            modalVisitChangeShow={modalVisitChangeShow}
            setModalVisitChangeShow={setModalVisitChangeShow}
            setScheduleBaselineModalShow={setScheduleBaselineModalShow}
            singlePhotoSession={infoData.single_photo_session}
          />
        )}
      </Box>

      {/* Unsaved changes dialog */}
      <Dialog
        open={modalVisitChangeShow}
        onClose={() => setModalVisitChangeShow(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Unsaved Changes
          <IconButton
            aria-label="close"
            onClick={() => setModalVisitChangeShow(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ py: 1 }}>
            You have Unsaved changes for the current opened Visit!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setHasChanged(false);
              setModalVisitChangeShow(false);
            }}
          >
            Discard Changes
          </Button>
          <Button variant="contained" onClick={() => setModalVisitChangeShow(false)}>
            Continue Making Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule baseline visit dialog */}
      <Dialog
        open={scheduleBaselineModalShow}
        onClose={() => setScheduleBaselineModalShow(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Baseline Visit Start Date
          <IconButton
            aria-label="close"
            onClick={() => setScheduleBaselineModalShow(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Start Date - This is the Start Date of the Baseline Visit
          </Typography>
          <CustomDatePicker
            label="Start Date"
            value={visitStartDateTime}
            onChange={(val) => setVisitStartDateTime(val)}
            mode={trial?.enforce_visit_start_time_at_12 ? "date" : "datetime"}
            variant="outlined"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              data-testid="schedule-baseline-save-btn"
              disabled={isTrialLocked}
              onClick={() => {
                handleBaselineStartDate();
                setScheduleBaselineModalShow(false);
              }}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FullForm;
